import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Loading } from '@/components/ui/loading'

interface RequirePlanProps {
  children: React.ReactNode
}

export const RequirePlan = ({ children }: RequirePlanProps) => {
  const [loading, setLoading] = useState(true)
  const [canAccess, setCanAccess] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const checkUserAccess = async () => {
      try {
        // 1. Verificar se é uma rota administrativa
        const isAdminRoute = location.pathname.startsWith('/admin')
        console.log(`[RequirePlan] Verificando acesso para rota: ${location.pathname}`)
        console.log(`[RequirePlan] É rota administrativa? ${isAdminRoute}`)
        
        // 2. Verificar usuário logado
        const { data: { user } } = await supabase.auth.getUser()
        
        if (!user) {
          console.log("[RequirePlan] Usuário não autenticado")
          setCanAccess(false)
          setLoading(false)
          return
        }
        
        console.log(`[RequirePlan] Usuário autenticado: ${user.id}`)
        
        // 3. Buscar informações completas do usuário
        const { data: userData, error } = await supabase
          .from('users')
          .select('id, email, role, current_plan')
          .eq('id', user.id)
          .single()

        if (error) {
          console.error("[RequirePlan] Erro ao buscar dados do usuário:", error)
          setCanAccess(false)
          setLoading(false)
          return
        }
        
        if (!userData) {
          console.error("[RequirePlan] Dados do usuário não encontrados")
          setCanAccess(false)
          setLoading(false)
          return
        }
        
        console.log("[RequirePlan] Dados do usuário recuperados:", {
          id: userData.id,
          email: userData.email,
          role: userData.role,
          plano: userData.current_plan
        })
        
        // 4. Lógica de acesso baseada no papel e plano do usuário
        const isAdmin = userData.role === 'admin'
        const hasPlan = !!userData.current_plan
        
        console.log(`[RequirePlan] Usuário é admin? ${isAdmin}`)
        console.log(`[RequirePlan] Usuário tem plano? ${hasPlan}`)
        
        // Casos em que o usuário pode acessar:
        // - É uma rota administrativa E o usuário é admin
        // - NÃO é uma rota administrativa E (o usuário tem plano OU é admin)
        // - Está tentando acessar a rota de seleção de plano
        const canAccessRoute = 
          (isAdminRoute && isAdmin) || 
          (!isAdminRoute && (hasPlan || isAdmin)) || 
          location.pathname === '/select-plan'
        
        console.log(`[RequirePlan] Usuário pode acessar a rota? ${canAccessRoute}`)
        setCanAccess(canAccessRoute)
      } catch (error) {
        console.error("[RequirePlan] Erro ao verificar acesso:", error)
        setCanAccess(false)
      } finally {
        setLoading(false)
      }
    }

    checkUserAccess()
  }, [location.pathname])

  if (loading) {
    console.log("[RequirePlan] Carregando...")
    return <Loading />
  }

  if (!canAccess) {
    // Se não pode acessar e não está na tela de seleção de plano, redireciona
    if (location.pathname !== '/select-plan') {
      console.log("[RequirePlan] Redirecionando para seleção de plano")
      return <Navigate to="/select-plan" replace />
    }
  }

  console.log("[RequirePlan] Renderizando conteúdo protegido")
  return <>{children}</>
} 